<script setup>
import { ref, onMounted, computed, inject, watchEffect, watch } from 'vue'
import { fetchWrapper } from '@/helpers/fetch-wrapper'
import { useAuthStore } from '@/stores/auth.store'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { loadLocaleMessages } from '../i18n'
import { IonModal, IonHeader, IonToolbar, IonButtons, IonButton, IonTitle, IonContent, IonPage, loadingController } from '@ionic/vue'
import { isIosApp, isAndroidApp, getLanguageIdByName, capitalizeFirstLetter } from '@/helpers/utils.js'
import { useRouter, useRoute } from 'vue-router'
import { getNode } from '@formkit/core'
import UserInterests from '@/components/UserInterests.vue'
import RegistrationGame from '@/components/RegistrationGame.vue'
import RewardAnimation from '@/components/RewardAnimation.vue'
import AchievementDisplay from '@/components/AchievementDisplay.vue'
import { isApp } from '@/helpers/utils.js'
import { analytics } from '@/helpers/analytics.js'

// const isApp = true
const email = ref('')
const realEmail = ref('')
const terms = ref(false)
const firstName = ref('')
const lastName = ref('')
const inviteCode = ref('')
const nativeLanguage = ref('')
const learnLanguage = ref('')
const learnLevel = ref('')
const emailErrors = ref([])
const realEmailErrors = ref([])
const languageOptions = ref([])
const languageOptionsEnglish = ref([])
const emailDone = ref(false)
const paymentSuccessful = ref(false)
const paymentError = ref(false)
const loginDisplay = ref(false)
const languages = ref(['en', 'es', 'fr', 'ja', 'pl', 'zh', 'it', 'ko'])
const hidden = ref(true)
const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()
const profile = ref({})
const attemptAppleBilling = ref(false)
const isLoading = ref(true)
const filterText = ref('')
const interestsLoaded = ref(false)
const closeRegisterModal = inject('closeRegisterModal')
const config = inject(Symbol.for('FormKitConfig'))
const gameCompleted = ref(false)
const startGame = ref(false)
/* just whether they have clicked to the next step, needed so we don't
load the RegistrationGame component until they have selected their
interests */
const selectedInterests = ref(false)
const gameStepsShown = ref(0)
var loading

if (!isApp) {
  hidden.value = false
} else {
  hidden.value = true
}
const { supportedLanguages, flags } = storeToRefs(useAuthStore())
const { t, locale } = useI18n()
const i18n = inject('i18n')
const skillLevelOptions = ref([
    { value: 'Beginner', label: 'profile.beginner' },
    { value: 'Intermediate', label: 'profile.intermediate' },
    { value: 'Advanced', label: 'profile.advanced' }
])

function closeAndLogin() {
  console.log('closeAndLogin called')
  if (isApp) {
    closeRegisterModal()
  }
  router.push('/login')
}

function completeGame() {
  console.log('completeGame called')
  fetchWrapper.post('/user-log/', {message: 'register-game-completed'})
  
  // Track tutorial completion
  analytics.trackTutorialComplete();
  
  gameCompleted.value = true
  startGame.value = false
  setTimeout(() => {
    const trophy = document.getElementById('trophy')
    if (trophy) {
      trophy.classList.remove('wiggle')
      // Force reflow to restart animation if needed
      void trophy.offsetWidth
      trophy.classList.add('wiggle')
    }
  }, 1000)
}

const currentStepName = ref(null)

onMounted(async () => {
    authStore.returnUrl = '/dashboard'
    if (isApp) {
        console.log('probably here 2')
        loading = await loadingController.create({
        duration: 0, // No auto-dismiss
        spinner: 'circles',
        cssClass: 'custom-loading-class',
        translucent: false,
        backdropDismiss: false
      })
      
      await loading.present()
      await new Promise(resolve => setTimeout(resolve, 1000))

      console.log('authStore.details registerpath2', authStore.details)
      if (authStore.user && authStore.details && authStore.details.registration_complete) {
          console.log('pushing to dashboard')
          loginDisplay.value = 'inline'
          closeRegisterModal()
          await loading.dismiss()
          router.push('/dashboard').then(failure => async () => {
            hidden.value = false
            loginDisplay.value = 'inline'
          })
          return
      } else {
          await setupRegisterForm()
          hidden.value = false
          loginDisplay.value = 'inline'
          await loading.dismiss()
      }
    } else {
        if (authStore.user && authStore.details && authStore.details.registration_complete) {
            loginDisplay.value = 'inline'
            router.push('/dashboard').then(failure => {
                hidden.value = false
                loginDisplay.value = 'inline'
            })
            return
        } else {
            console.log('authStore.details', JSON.stringify(authStore.details))
            console.log('probably here')
            await setupRegisterForm()
            loginDisplay.value = 'block'
        }
    }
    console.log('looks like displaying register form')
    var interval = setInterval(() => {
      const node = getNode('multi-step')
      if (node && node.children) {
        clearInterval(interval)
        watchEffect(() => {
          console.log('watchEffect called for multi-step node')
          if (!node || !node.children) {
            console.error("Node not found for multi-step watcher")
            return
          }
          const activeStep = node.children.find(child => child.context?.isActiveStep)
          console.log('after')
          currentStepName.value = activeStep?.name || null
        })
      } else {
        console.error("Node not found for multi-step, try again")
      } 
    }, 100)
})

const getLearnLanguages = computed(() => {
  const filter = filterText.value.trim().toLowerCase()
  return languageOptionsEnglish.value
    .filter(language => language.value !== nativeLanguage.value)
    .filter(language => !filter || language.label.toLowerCase().includes(filter))
})

async function setupRegisterForm() {
  console.log('setupRegisterForm')
  await load_profile()
  if (profile.value.email) {
    email.value = profile.value.email
    terms.value = true
    attemptAppleBilling.value = profile.value.skip_billing_prompt !== true
  }
  for (const [key, value] of Object.entries(supportedLanguages.value)) {
    languageOptionsEnglish.value.push({ flag: value, value: key, label: capitalizeFirstLetter(t(`language.name.${getLanguageIdByName(key)}`)) + " (" + flags.value[value].display + ")"})
    languageOptionsEnglish.value.sort((a, b) => (a.label > b.label) ? 1 : -1)
  }
  if (localStorage.getItem('language')) {
    for (const key in supportedLanguages.value) {
      if (supportedLanguages.value[key] === localStorage.getItem('language')) {
        nativeLanguage.value = key
      }
    }
  }
  isLoading.value = false
  if (profile.value.learning_language) {
    learnLanguage.value = profile.value.learning_language
    setTimeout(() => {
        goToNextStep()
        setTimeout(() => {
            goToNextStep()
        }, 100)
    }, 100)
  }
  if (profile.value.learning_level) {
    learnLevel.value = capitalizeFirstLetter(profile.value.learning_level)
    setTimeout(() => {
        goToNextStep()
    }, 100)
  }
  if (profile.value.has_interests) {
    // land on the pre-game page
    setTimeout(() => {
        interestsLoaded.value = true
        goToNextStep()
    }, 100)
  }
  if (profile.value.first_name && profile.value.last_name) {
    console.log('profile has first and last name, going to email step')
    firstName.value = profile.value.first_name
    lastName.value = profile.value.last_name
    setTimeout(() => {
        goToNextStep()
        setTimeout(() => {
          goToNextStep()
          setTimeout(() => {
              goToNextStep()
          }, 100)
        }, 100)
    }, 100)
  }
}
async function load_profile() {
  try {
    const response = await fetchWrapper.get(`/profile/`)
    profile.value = response
  } catch (error) {
    console.log('error', error)
  }
}
function getFlag (language) {
  if (flags.value[language]) {
    return flags.value[language].flag
  }
}

// Method to handle selection and move to next step
function selectLearnLanguage(option) {
  console.log('Selected language:', option)
  learnLanguage.value = option.value
  learnLevel.value = ''
  selectedInterests.value = false
  startGame.value = false
  // Move to next step in FormKit multi-step
  const node = getNode('multi-step')
  if (node) {
    console.log("node found, moving to next step")
    node.next()
  }
}
function selectSkillLevel(option) {
  learnLevel.value = option.value
  console.log("select skill level", option.value)
  // Move to next step in FormKit multi-step
  const node = getNode('multi-step')
  if (node) node.next()
}
async function checkValidation(currentStep, targetStep, delta) {
    if (delta < 0) {
      console.log('checkValidation going back to previous step:', currentStep.id, 'target:', targetStep.id, 'no validation needed')
        return true
    }
    if (delta > 1) {
      return false
    }
    console.log('delta', delta)
    /* 
    This function is called before changing steps in the multi-step form.
    - learnLanguage  (after this is selected, we create an anonymous user)
    - learnLevel (after updated, we update the profile))
     - interests (select and let them move on)
    */
    console.log('checkValidation called for step:', currentStep.id, currentStep, targetStep.id)
    if (currentStep.id === 'welcome') {
    } else if (currentStep.id === 'learnLanguage') {
        if (!learnLanguage.value) {
            return false
        }
        // Create anonymous user if not already done
        if (!authStore.user || !authStore.user.id) {
            const languageCode = localStorage.getItem('language')
            var timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (email.value) {
                console.log('email already set', email.value, 'waiting')
            } else {
                email.value = `anonymous-${Date.now()}@lingo.ing`
                const result = await authStore.register(email.value, languageCode, timezone).catch( 
                    function (error) { 
                        emailErrors.value = [error]
                    }
                ).then(() => {
                    // Track sign_up event
                    analytics.trackSignUp('anonymous');
                    
                    gtag('event', 'conversion', {
                        'send_to': 'AW-16765928710/OvIuCIWAzOYZEIaKz7o-',
                        'value': 1.0,
                        'currency': 'CAD'
                    });
                })
            }
        }
        fetchWrapper.post('/user-log/', {message: 'select-skill-shown'})
        return true
    } else if (currentStep.id === 'languageSkill') {
        if (!learnLevel.value) {
            return false
        }
        // Update profile with selected language and skill level
        authStore.updateRegistration({nativeLanguage: nativeLanguage.value, learnLanguage: learnLanguage.value, learnLevel: learnLevel.value}, true) 
        interestsLoaded.value = true
        fetchWrapper.post('/user-log/', {message: 'select-interests-shown'})
        return true
    } else if (currentStep.id === 'interests') {
        selectedInterests.value = true
        try {
          await fetchWrapper.post('/register/initialize-user-curriculum/')
        }
        catch (error) {
          // it's ok if this fails, it just means the user already has a curriculum
        }
        fetchWrapper.post('/user-log/', {message: 'game-intro-shown'})
        return true
    } else if (currentStep.id === 'game_intro') {
        // This step is just for showing the game intro, no validation needed
        startGame.value = true
        fetchWrapper.post('/user-log/', {message: 'game-shown'})
        return true
    } else if (currentStep.id === 'game') {
        // This step is just for showing the game, no validation needed
        fetchWrapper.post('/user-log/', {message: 'register-shown-profile-form'})
        return true
    } else if (currentStep.id === 'profile') {
        authStore.updateRegistration({firstName: firstName.value, lastName: lastName.value}, true)
        fetchWrapper.post('/user-log/', {message: 'name-completed'})
        return true
    } else if (currentStep.id === 'emailStep') {
        fetchWrapper.post('/user-log/', {message: 'email-completed'})
        authStore.updateRegistration({email: realEmail.value}, true).catch(
            function (error) {
                realEmailErrors.value = [error]
            }
        )
        closeRegisterModal()
        return true
    }
    return true
}
function goToNextStep() {
  const node = getNode('multi-step')
  if (node) {
    console.log("goToNextStep called, moving to next step")
    node.next()
  } else {
    console.error("Node not found for multi-step gotoNextStep")
  }
}
function checkDebugEmail () {
  if (realEmail.value === 'debug') {
    realEmail.value = `debug${Date.now()}@lingo.ing`
  }
}
function submitHandler() {
    if (!authStore.returnUrl) {
        authStore.returnUrl = '/dashboard'
    }
    console.log('submitHandler called')
    authStore.updateRegistration({email: realEmail.value}, false).catch(
        function (error) {
            console.error('Error updating registration:', error)
            realEmailErrors.value = [error]
        }
    ).then(() => {
        console.log('Registration updated successfully')
        // Track sign_up with email method
        analytics.trackSignUp('email');
        
        closeRegisterModal()
        if (!authStore.returnUrl) {
            authStore.returnUrl = '/dashboard'
        }
    })
}
function languageDisplay (language) {
  if (flags.value[language]) {
    return flags.value[language].display
  }
}
async function updateLanguage (language) {
  await loadLocaleMessages(i18n, language)
  locale.value = language
  localStorage.setItem('language', language)
  config.locale = language
  location.reload()
}

const hideNextButton = computed(() => {
  // eehhh let them out at this point
  if (gameStepsShown.value >= 10) {
    return false
  }
  else if (currentStepName.value === 'game' && !gameCompleted.value) {
    return true
  }
  return false
})

function onFilterInput(e) {
  filterText.value = e.target.value
}
function incrementGameSteps() {
  gameStepsShown.value += 1
  console.log('incrementGameSteps called, current steps shown:', gameStepsShown.value)
}
async function checkInviteCode () {
  if (inviteCode.value === 'tester' || inviteCode.value === 'Tester') {
    paymentSuccessful.value = true
    const response = await fetchWrapper.post(`/tester/create-subscription/`, {})
  }
}
</script>

<template>
  <section v-if="hidden === false" :class="['loginForm', 'mb-4',  isApp ? 'is-in-app' : '']" :style="{display: loginDisplay}">
    <div v-if="!isLoading" :class="['container', hideNextButton ? 'hide-next-button' : '', isApp ? 'is-in-app' : '']">
      <div class="row justify-content-center mb-2">
        <div v-if="isApp === false" class="col-xl-6 col-sm-8">
          <h1 class="h1-title text-center">{{ t('register.lets-get-started') }}</h1>
        </div>
      </div>
      <div class="row justify-content-center">
        <div class="col-xl-8 col-sm-12 col-md-8 align-self-center justify-content-center">
          <FormKit :actions="false" type="form" @submit="submitHandler" form-class="outer-container justify-content-center" outer-classes="p-0">
            <FormKit form-class="multi-container" id="multi-step" type="multi-step" tab-style="progress" :allow-incomplete="false" :before-step-change="({ currentStep, targetStep, delta }) => { return checkValidation(currentStep, targetStep, delta) }" outer-class="row justify-content-center">
              <FormKit type="step" id="welcome" name="welcome" :label="t('register.language-details')">
                <div class="container-with-signup">
                  <h5 class="fnt-weight-600 mb-4">{{ t('tour.welcome-to-lingo-ing') }}</h5>
                  <img width="250px" src="@/assets/images/icons/welcome.png" class="mx-auto"/>
                  <div class="mt-3 px-2 mx-3">{{ t('register.we-are-going-to-ask-you-a-few') }}</div>
                  <div v-if="isApp" class="signup-options">
                    <div class="loginForm-signup-link mt-2">
                      <p>{{ t('register.already-a-member') }}<br>
                        <a v-if="isApp" to="/login" class="text-primary-cs href" @click="closeAndLogin()"> {{ t('register.log-in-here') }}!</a>
                        <router-link v-else to="/login" class="text-primary-cs href" @click="closeAndLogin()"> {{ t('register.log-in-here') }}!</router-link>
                      </p>
                    </div>
                    <div class="languageSelector">
                      <CDropdown toggerText="Language" variant="nav-item" direction="dropup">
                        <CDropdownToggle togglerText="Dropdown">
                          <CButton>
                            <country-flag v-if="getFlag(locale)" :country="getFlag(locale)" size="normal" />
                            {{ languageDisplay(locale) }}
                          </CButton>
                        </CDropdownToggle>
                        <CDropdownMenu class="overflow-auto">
                          <CDropdownItem  v-for="language in languages" :key="language" @click="updateLanguage(language)">
                            <CDropdownDivider />
                            <country-flag v-if="getFlag(language)" :country="getFlag(language)" size="normal" />
                            {{ languageDisplay(language) }}
                          </CDropdownItem>
                        </CDropdownMenu>
                      </CDropdown>
                    </div>
                  </div>
                </div>
              </FormKit>
              <FormKit type="step" id="learnLanguage" name="learnLanguage" :label="t('register.language-details')">
                <!-- Hidden email field for FormKit form state and error display -->
                <FormKit
                  type="hidden"
                  name="email"
                  v-model="email"
                />
                <FormKit
                  type="hidden"
                  name="learnLanguage"
                  v-model="learnLanguage"
                />
                <h5 class="fnt-weight-600 mb-4">{{ t('register.what-would-you-like-to-learn-') }}</h5>
                  <div class="input-group mb-3">
                    <span class="input-group-text">
                      <i class="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      class="form-control"
                      :placeholder="t('register.search')"
                      v-model="filterText"
                      @input="onFilterInput"
                      @change="onFilterInput"
                      @compositionend="onFilterInput"
                    />
                  </div>
                <div class="dropdown-menu show" style="position: static; float: none;">
                  <!-- Filter box styled like CoreUI input group -->
                  <div v-if="getLearnLanguages.length === 0" class="dropdown-item d-flex align-items-center" style="cursor: pointer;">
                    {{ t('register.no-results-found') }}
                  </div>
                   <div class="languagelist">
                    <template v-for="(option, idx) in getLearnLanguages" :key="option.value">
                      <div
                        class="dropdown-item d-flex align-items-center"
                        style="cursor: pointer;"
                        @click="selectLearnLanguage(option)"
                      >
                        <country-flag v-if="getFlag(option.flag)" :country="getFlag(option.flag)" size="normal" class="me-2" />
                        <span>{{ option.label }}</span>
                      </div>
                      <div v-if="idx < getLearnLanguages.length - 1" class="dropdown-divider"></div>
                    </template>
                  </div>
                </div>
                <!-- Optional: Show visible error message -->
                <div v-if="emailErrors.length" class="text-danger mt-2">
                  <div v-for="err in emailErrors" :key="err">{{ err }}</div>
                </div>
              </FormKit>
              <FormKit type="step" id="languageSkill" name="languageSkill" :label="t('register.skill-level')">
                <b v-if="learnLanguage">{{ t(`language.name.${getLanguageIdByName(learnLanguage)}`) }}</b> {{ t('register.is-a-great-language-to-learn') }}<br><br>
                <h5 class="fnt-weight-600 mb-4">{{ t('register.whats-your-current-skill-level-in-learnlanguage', {learnLanguage: learnLanguage}) }}</h5>
                <div class="dropdown-menu show short" style="position: static; float: none;">
                  <template v-for="(option, idx) in skillLevelOptions" :key="option.value">
                    <div
                      class="dropdown-item d-flex align-items-center"
                      style="cursor: pointer;"
                      @click="selectSkillLevel(option)"
                    >
                    <img v-if="option.value === 'Beginner'" width="50px" src="@/assets/images/icons/Beginner.webp" style="margin-right: 10px"/>
                    <img v-if="option.value === 'Advanced'" width="50px" src="@/assets/images/icons/Advanced.webp" style="margin-right: 10px"/>
                    <img v-if="option.value === 'Intermediate'" width="50px" src="@/assets/images/icons/Intermediate.webp" style="margin-right: 10px"/>
                      <span>{{ t(`${option.label}`) }}</span>
                    </div>
                    <div v-if="idx < skillLevelOptions.length" class="dropdown-divider"></div>
                  </template>
                </div>
                <br>
              </FormKit>
              <FormKit type="step" id="interests" name="interests" :label="t('register.your-interests')">
                <UserInterests v-if="interestsLoaded" />
              </FormKit>
              <FormKit type="step" id="game_intro" name="game_intro" :label="t('register.game')">
                <h5 class="fnt-weight-600 mb-4">{{ t('register.great') }}</h5>
                <div class="mt-3 px-2 mx-3 mb-3">{{ t('register.weve-got-a-personalized-learning-plan-just-for') }}</div>
                <img width="250px" src="@/assets/images/icons/Studies.png" style="margin-right: 10px"/>
                <div class="mt-3 px-2 mx-3">{{ t('register.well-start-with-a-fun-game-to-help') }}</div>
              </FormKit>
              <FormKit type="step" id="game" name="game" :label="t('register.game')">
                <RegistrationGame v-if="interestsLoaded && selectedInterests && startGame && !gameCompleted" @topicCompleted="completeGame" @incrementGameStepsShown="incrementGameSteps" />
                <div v-else>
                  <reward-animation v-if="gameCompleted" type="confetti" severity="major" />
                  <h5 class="fnt-weight-600 mb-4">{{ t('register.congratulations') }}</h5>
                  <div>{{ t('register.you-have-completed-your-first-lesson') }}</div>
                  <img id="trophy" width="150px" src="@/assets/images/icons/trophy.png" style="margin-right: 10px" class="mb-1"/>
                  <AchievementDisplay v-if="gameCompleted" :mini="true"  />
                  <div class="mt-2">{{ t('register.practice-every-day-to-keep-your-streak-going') }}</div>
                </div>
              </FormKit>
              <FormKit type="step" id="profile" name="profile" :label="t('register.your-details')">
                <h5 class="fnt-weight-600 mb-4">{{ t('register.create-a-profile-to-save-your-progress-') }}</h5>
                <FormKit 
                  v-model="firstName"
                  type="text" 
                  :placeholder="t('register.your-first-name')" 
                  validation="required"
                  @keydown.enter.prevent="goToNextStep"
                  @keyup.enter.prevent="goToNextStep"
                />
                <FormKit 
                  v-model="lastName"
                  type="text" 
                  :placeholder="t('register.your-last-name')" 
                  validation="required"
                  @keydown.enter.prevent="goToNextStep"
                  @keyup.enter.prevent="goToNextStep"
                />
                <FormKit v-if="!isIosApp"
                  v-model="inviteCode"
                  type="text" 
                  :placeholder="t('register.your-invite-code---if-you-received-one-')" 
                  :help="t('register.this-field-is-optional-')"
                  @blur="checkInviteCode()"
                  @keydown.enter.prevent="goToNextStep"
                  @keyup.enter.prevent="goToNextStep"
                />
              </FormKit>
              <FormKit type="step" id="emailStep" name="emailStep" :label="t('register.what-is-your-email')">
                <h5 class="fnt-weight-600 mb-4">{{ t('register.what-is-your-email-address-') }}</h5>
                <FormKit 
                  name="email"
                  type="email" 
                  :placeholder="t('register.please-enter-your-email')" 
                  v-model="realEmail"
                  :errors="realEmailErrors"
                  validation="required|email" 
                  @change="checkDebugEmail()"
                  @keydown.enter.prevent="goToNextStep"
                  @keyup.enter.prevent="goToNextStep"
                />
                <template #stepNext>
                  <FormKit type="submit" />
                </template>
              </FormKit>
            </FormKit>
          </FormKit>
        </div>
      </div>
    </div>
  </section>
  <div v-if="isApp === false" class="cityBgBottom"></div>
</template>

<style scoped>
/* Copy your styles from RegisterForm.vue here */
.dropdown-menu.show {
  max-height: 300px;
  min-height: 300px;
  overflow-x: auto;
}
.dropdown-menu.show.short {
  max-height: 300px;
  min-height: 300px;
  overflow-x: auto;
}
div.formkit-step {
  position: relative;
  /* height: 500px;
  min-height: 500px;
  max-height: 500px; */
  display: flex;
  flex-direction: column;
}
::v-deep div.formkit-step-inner {
  height: 450px;
  min-height: 450px;
  max-height: 450px;
  /* background-color:  yellow; */
}
.is-in-app :deep(.formkit-step-inner) {
  /* background-color: green !important; */
  /* overflow-y: auto;  */
  min-height: 75vh !important;
  max-height: 75vh !important;
}
.is-in-app :deep(.formkit-steps) {
  /* background-color: green !important; */
  /* overflow-y: auto;  */
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.is-in-app {
  padding-left: 0 !important;
  padding-right: 0 !important;

}
.is-in-app :deep(.instructions-row) {
  /* background-color: green !important; */
  /* overflow-y: auto;  */
  margin-top: 10px;
}
.container-with-signup {
  display: flex;
  flex-direction: column;
  min-height: 75vh;
}
.signup-options {
  margin-top: auto;
}

::v-deep div.formkit-step-actions {
  margin-top: auto;
  position: relative !important;
  /* // background-color: red; */
}
section.is-in-app {
  height: 100vh;
}
div.is-in-app {
  height: 100%;
}
@keyframes wiggle {
  0% { transform: rotate(0deg);}
  15% { transform: rotate(-15deg);}
  30% { transform: rotate(10deg);}
  45% { transform: rotate(-10deg);}
  60% { transform: rotate(6deg);}
  75% { transform: rotate(-4deg);}
  100% { transform: rotate(0deg);}
}
#wiggle,
.wiggle {
  animation: wiggle 0.7s;
}
</style>
<style>
.hide-next-button .formkit-step-actions, .hide-next-button .loginForm-signup-link, .hide-next-button .languageSelector {
  display: none !important;
}
</style>
